import { createSlice } from "@reduxjs/toolkit";
import {
  computeIsComplete,
  computeOverallScore,
  computeScoresFromCaptured,
  filterCapturedToFrameworkItems,
} from "../../utils/chatUtils";

const initialState = {
  projectId: null,
  projectName: "",
  sessionId: null,
  frameworkSections: [],
  scores: {},
  captured: {},
  gate0Data: {},
  gate0Items: 0,
  startedAt: null,
  messages: [],
  scorePills: [],
  outputs: null,
  overallScore: 0,
  isComplete: false,
  turns: 0,
  nextTarget: null,
  openSections: [],
  status: "idle",
  error: null,
};

const intakeSlice = createSlice({
  name: "intake",
  initialState,
  reducers: {
    resetIntake: () => initialState,
    setIntakeProject: (state, action) => {
      state.projectId = action.payload.projectId;
      state.projectName = action.payload.projectName || "";
    },
    setIntakeSession: (state, action) => {
      state.sessionId = action.payload.sessionId;
    },
    setIntakeStatus: (state, action) => {
      state.status = action.payload;
    },
    setIntakeError: (state, action) => {
      state.error = action.payload;
    },
    addIntakeMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    addScorePill: (state, action) => {
      state.scorePills.push(action.payload);
    },
    setScorePills: (state, action) => {
      // Replace the whole batch — one pill per section for the latest update only.
      state.scorePills = action.payload || [];
    },
    clearScorePills: (state) => {
      state.scorePills = [];
    },
    toggleOpenSection: (state, action) => {
      const id = action.payload;
      const isOnlyOpen = state.openSections.length === 1 && state.openSections[0] === id;
      state.openSections = isOnlyOpen ? [] : [id];
    },
    setOpenSections: (state, action) => {
      state.openSections = action.payload || [];
    },
    applyIntakeState: (state, action) => {
      const data = action.payload;
      if (!data) return;
      if (data.framework_sections) state.frameworkSections = data.framework_sections;
      if (data.captured !== undefined) {
        // Agent chat/extraction/reset returns a full session snapshot — replace so
        // cleared items disappear from the sidebar without a page reload.
        state.captured = data.replace_captured
          ? { ...data.captured }
          : { ...state.captured, ...data.captured };
      }
      if (data.gate0_data) state.gate0Data = data.gate0_data;
      if (typeof data.gate0_items === "number") state.gate0Items = data.gate0_items;
      if (data.started_at) state.startedAt = data.started_at;
      if (typeof data.turns === "number") state.turns = data.turns;
      if (data.next_target !== undefined) {
        const targetId =
          typeof data.next_target === "string"
            ? data.next_target
            : data.next_target?.id;
        const matched = state.frameworkSections.find((s) => s.id === targetId);
        if (matched) {
          state.nextTarget = { id: matched.id, name: matched.name };
        } else if (targetId && data.next_target_name) {
          state.nextTarget = { id: targetId, name: data.next_target_name };
        } else {
          state.nextTarget = data.next_target;
        }
      }
      if (data.open_sections) state.openSections = data.open_sections;

      const sections = state.frameworkSections;
      if (sections?.length) {
        state.captured = filterCapturedToFrameworkItems(state.captured, sections);
        const computed = computeScoresFromCaptured(state.captured, sections);
        const agentScores = data.scores || {};
        const mergedScores = {};
        sections.forEach((s) => {
          if (data.replace_captured) {
            mergedScores[s.id] =
              agentScores[s.id] ?? computed[s.id] ?? 0;
          } else {
            mergedScores[s.id] = Math.max(
              computed[s.id] || 0,
              agentScores[s.id] || 0,
              state.scores[s.id] || 0
            );
          }
        });
        state.scores = mergedScores;
        state.overallScore = computeOverallScore(mergedScores, sections);
        state.isComplete =
          typeof data.is_complete === "boolean"
            ? data.is_complete
            : computeIsComplete(mergedScores, sections, state.captured);
      } else {
        if (data.scores) {
          state.scores = data.replace_captured
            ? { ...data.scores }
            : { ...state.scores, ...data.scores };
        }
        if (typeof data.overall_score === "number") state.overallScore = data.overall_score;
        if (typeof data.is_complete === "boolean") state.isComplete = data.is_complete;
      }
    },
    setIntakeOutputs: (state, action) => {
      state.outputs = action.payload;
    },
    clearIntakeTranscript: (state) => {
      state.messages = [];
      state.scorePills = [];
    },
    truncateIntakeFromMessage: (state, action) => {
      const messageId = action.payload;
      const idx = state.messages.findIndex((m) => m.id === messageId);
      if (idx < 0) return;
      state.messages = state.messages.slice(0, idx);
      state.scorePills = [];
      state.outputs = null;
      state.isComplete = false;
    },
    setIntakeMessages: (state, action) => {
      state.messages = action.payload || [];
    },
  },
});

export const {
  resetIntake,
  setIntakeProject,
  setIntakeSession,
  setIntakeStatus,
  setIntakeError,
  addIntakeMessage,
  addScorePill,
  setScorePills,
  clearScorePills,
  toggleOpenSection,
  setOpenSections,
  applyIntakeState,
  setIntakeOutputs,
  clearIntakeTranscript,
  truncateIntakeFromMessage,
  setIntakeMessages,
} = intakeSlice.actions;

export default intakeSlice.reducer;
